import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2 } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_2f371d17-d24a-497f-bf8d-0184002770c6/artifacts/5nav51b9_logo-print-hd.jpg";
const BG_IMAGE = "https://images.pexels.com/photos/8357141/pexels-photo-8357141.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";

export function LoginPage() {
    const { user, loading, login } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // If still checking auth, show loading
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" />
            </div>
        );
    }

    // If already logged in, redirect to dashboard
    if (user) {
        return <Navigate to="/" replace />;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        const result = await login(email, password);
        
        if (!result.success) {
            setError(result.error);
        }
        
        setIsSubmitting(false);
    };

    return (
        <div className="min-h-screen flex" data-testid="login-page">
            {/* Left Panel - Background Image (hidden on mobile) */}
            <div 
                className="hidden lg:flex lg:w-1/2 relative"
                data-testid="login-left-panel"
            >
                <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${BG_IMAGE})` }}
                />
                <div className="absolute inset-0 bg-[#2e7cd6]/20" />
                <div className="relative z-10 flex flex-col justify-center items-center w-full p-12">
                    <h1 className="text-4xl font-bold text-white text-center tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Gestion Big Déménagement
                    </h1>
                    <p className="mt-4 text-lg text-white/90 text-center max-w-md">
                        Plateforme de gestion pour votre entreprise de déménagement
                    </p>
                </div>
            </div>

            {/* Right Panel - Login Form */}
            <div 
                className="flex-1 flex items-center justify-center p-6 bg-slate-50"
                data-testid="login-right-panel"
            >
                <Card className="w-full max-w-md shadow-lg border-slate-200">
                    <CardHeader className="text-center pb-2">
                        <img 
                            src={LOGO_URL} 
                            alt="Logo Big Déménagement" 
                            className="h-20 w-auto mx-auto mb-4"
                            data-testid="login-logo"
                        />
                        <CardTitle 
                            className="text-2xl font-bold text-slate-900 tracking-tight"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                            Connexion
                        </CardTitle>
                        <CardDescription className="text-slate-500">
                            Connectez-vous à votre espace de gestion
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
                            {error && (
                                <div 
                                    className="p-3 rounded-lg bg-red-50 text-red-600 text-sm"
                                    data-testid="login-error"
                                >
                                    {error}
                                </div>
                            )}
                            
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-slate-700">
                                    Email
                                </Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="votre@email.fr"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="border-slate-200 focus:border-[#2e7cd6] focus:ring-[#2e7cd6]"
                                    data-testid="login-email-input"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-slate-700">
                                    Mot de passe
                                </Label>
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="border-slate-200 focus:border-[#2e7cd6] focus:ring-[#2e7cd6]"
                                    data-testid="login-password-input"
                                />
                            </div>
                            
                            <Button
                                type="submit"
                                className="w-full bg-[#2e7cd6] hover:bg-blue-700 text-white font-medium"
                                disabled={isSubmitting}
                                data-testid="login-submit-button"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Connexion...
                                    </>
                                ) : (
                                    'Se connecter'
                                )}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
